import { RefObject, useEffect, useRef } from 'react';

import { useToast } from '@act/react-components/app/ReusableToast';
import {
  CreateVisitForm,
  ScheduledEventDto,
  UpdateVisitResponseDto,
  UserDto,
  VisitCreationRequestDto,
  VisitDto,
  VisitUpdateRequestDto,
} from '@act/shared/data-transfer-objects';
import { visitsRequests } from 'redux/requests/visits.request';
import {
  createVisitSuccess,
  deleteVisit,
  updateVisitSuccess,
} from 'redux/slices/visitsSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { awaitFlushPendingNoteAutosave } from '../flushPendingNoteAutosave.util';
import { handleNoteFieldClose } from '../noteFieldClose.util';
import { NoteFieldRef } from '../NoteField';
import { useVisitNoteSaveOverride } from './useVisitNoteSaveOverride.hook';
import { handleHospitalizationFollowUpCallSaveResult } from '../../member-page/Calls/hospitalizationFollowUpCallSave.util';
import useConfirmEngagementDialog from './useConfirmEngagementDialog.hook';
import useUnsavedChangesDialog from './useUnsavedChangesDialog.hook';
import {
  buildVisitDialogActions,
  buildVisitSaveRequestBody,
  getVisitDialogTitle,
  VisitOutcomeLike,
} from '../visitDialog.util';

/** Params for {@link useVisitDialogPersistence}. */
export type UseVisitDialogPersistenceParams = {
  user: UserDto | undefined;
  visit?: VisitDto;
  mode: 'Create' | 'Edit';
  scheduledEvent?: ScheduledEventDto;
  handleClose: () => void;
  completionCallback?: () => void;
  canDelete: boolean;
  outcomes: VisitOutcomeLike[];
  formIsDirty: boolean;
  setConfirmDeleteOpen: (open: boolean) => void;
  noteFieldRef: RefObject<NoteFieldRef | null>;
};

/**
 * Save, delete, and API side effects for the visit dialog.
 * @param params Visit context, form accessors, and dialog callbacks.
 * @returns Persistence handlers and footer actions.
 */
export function useVisitDialogPersistence({
  user,
  visit,
  mode,
  scheduledEvent,
  handleClose,
  completionCallback,
  canDelete,
  outcomes,
  formIsDirty,
  setConfirmDeleteOpen,
  noteFieldRef,
}: UseVisitDialogPersistenceParams) {
  const openConfirmEngagementDialog = useConfirmEngagementDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const hospitalizationFollowUpRequestedRef = useRef(false);

  const [createdVisit, isCreatingVisit, createVisit] = useApiCall(
    visitsRequests.createVisit
  );
  const [updatedVisit, isUpdatingVisit, updateVisit] =
    useApiCall(visitsRequests.updateVisit);

  const isSaving = isCreatingVisit || isUpdatingVisit;

  const { visitNoteSaveOverride } = useVisitNoteSaveOverride({
    visitId: visit?.id,
    enabled: mode === 'Edit',
  });

  useEffect(() => {
    if (createdVisit) {
      dispatch(createVisitSuccess(createdVisit));
      noteFieldRef.current?.clearDraft();
      if (hospitalizationFollowUpRequestedRef.current && user?.id) {
        handleHospitalizationFollowUpCallSaveResult(
          createdVisit.hospitalizationFollowUpStatus,
          user.id,
          dispatch
        );
        hospitalizationFollowUpRequestedRef.current = false;
      }
      toast('Visit created successfully');
      handleClose();
    }
    if (updatedVisit) {
      dispatch(updateVisitSuccess(updatedVisit));
      if (hospitalizationFollowUpRequestedRef.current && user?.id) {
        handleHospitalizationFollowUpCallSaveResult(
          (updatedVisit as UpdateVisitResponseDto)
            .hospitalizationFollowUpStatus,
          user.id,
          dispatch
        );
        hospitalizationFollowUpRequestedRef.current = false;
      }
      toast('Visit updated successfully');
      handleClose();
    }
  }, [
    createdVisit,
    updatedVisit,
    dispatch,
    user?.id,
    toast,
    handleClose,
    noteFieldRef,
  ]);

  async function handleSave(data: CreateVisitForm) {
    if (isSaving || !user?.id) return;

    const selectedOutcome = outcomes.find(
      outcome => outcome.id === data.outcomeId
    );
    if (
      user.memberStage.key === 'outreach' &&
      selectedOutcome?.key === 'engaged'
    ) {
      const confirmed = await openConfirmEngagementDialog();
      if (!confirmed) {
        return;
      }
    }

    if (mode === 'Edit') {
      await awaitFlushPendingNoteAutosave(noteFieldRef);
    }

    const body = buildVisitSaveRequestBody({
      data,
      userId: user.id,
      visit,
      mode,
      scheduledEvent,
      selectedOutcomeText: selectedOutcome?.text,
    });

    hospitalizationFollowUpRequestedRef.current = Boolean(
      data.isHospitalizationFollowUp
    );

    try {
      if (mode === 'Edit') {
        updateVisit({
          body: body as VisitUpdateRequestDto,
          params: { visitId: visit?.id },
        });
      } else {
        createVisit({ body: body as VisitCreationRequestDto });
      }
    } catch (err) {
      console.error(`Error saving visit: ${err}`);
    }

    completionCallback?.();
  }

  const handleDeleteVisit = () => {
    if (!visit?.id) return;

    dispatch(
      deleteVisit({
        params: {
          visitId: visit.id,
        },
      })
    );
    handleClose();
  };

  const handleFormClose = () => {
    handleNoteFieldClose({
      mode: mode === 'Edit' ? 'edit' : 'create',
      isDirty: formIsDirty,
      noteFieldRef,
      handleUnsavedChanges,
      finishClose: handleClose,
    });
  };

  const actions = buildVisitDialogActions({
    canDelete,
    isSaving,
    handleFormClose,
    onDeleteClick: () => setConfirmDeleteOpen(true),
  });

  const title = getVisitDialogTitle(
    mode,
    user?.firstName,
    user?.lastName
  );

  return {
    actions,
    handleDeleteVisit,
    handleFormClose,
    handleSave,
    isSaving,
    title,
    visitNoteSaveOverride,
  };
}
